/**
 * Java Symbol Index Service
 *
 * Fetches and consumes the job-scoped java_symbol_index.json built by
 * Pre-Flight during (or after) a compile run.
 *
 * S3 path:  {job_artifacts_prefix}/java_symbol_index.json
 * API path: GET /pre-flight/results/json/{jobId}/java_symbol_index.json
 * Rebuild:  POST /pre-flight/{jobId}/reindex
 *
 * Schema v1:
 *   { schema_version, generated_at, job_id, file_count, perFile,
 *     typesBySimpleName, typesByFqn, fieldsByName, methodsByName }
 *
 * All functions are non-fatal — callers must handle null gracefully.
 */

import { getAuthHeaders } from './nodeExecutors/authHelper'

const hdrs = () => getAuthHeaders({ 'Content-Type': 'application/json' })

// ── Fetch ─────────────────────────────────────────────────────────────────────

/**
 * Fetch the symbol index for a Pre-Flight job.
 * Returns the parsed index object, or null if missing / unavailable.
 *
 * @param {string} jobId
 * @param {string} apiBase
 * @returns {Promise<object|null>}
 */
export async function fetchSymbolIndex(jobId, apiBase) {
  if (!jobId || !apiBase) return null
  try {
    const res = await fetch(
      `${apiBase}/pre-flight/results/json/${jobId}/java_symbol_index.json`,
      { headers: hdrs() }
    )
    if (res.status === 404) return null          // not yet built — graceful
    if (!res.ok) {
      console.warn('[SymbolIndex] fetch failed:', res.status)
      return null
    }
    return await res.json()
  } catch (err) {
    console.warn('[SymbolIndex] fetch error:', err?.message)
    return null
  }
}

// ── Rebuild (Re-index button) ─────────────────────────────────────────────────

/**
 * Trigger a server-side rebuild of the symbol index for a job.
 * Downloads workspace/ files and re-runs tree-sitter on the server.
 *
 * @param {string} jobId
 * @param {string} apiBase
 * @returns {Promise<{status, job_id, file_count, built_at}>}
 * @throws on network/server error
 */
export async function rebuildSymbolIndex(jobId, apiBase) {
  const res = await fetch(`${apiBase}/pre-flight/${jobId}/reindex`, {
    method: 'POST',
    headers: hdrs(),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || `Reindex failed (${res.status})`)
  }
  return res.json()
}

// ── Symbol resolution ─────────────────────────────────────────────────────────

/**
 * Look up a symbol name in the index.
 *
 * Returns:
 *   { found: true,  kind: 'type'|'field'|'method', locations: [...] }
 *   { found: false, kind: null, locations: [] }
 *
 * @param {string} name  — bare symbol name (e.g. "BigDecimal", "taxRate")
 * @param {object} index — schema v1 index object
 * @returns {{ found: boolean, kind: string|null, locations: any[] }}
 */
export function resolveSymbol(name, index) {
  if (!name || !index) return { found: false, kind: null, locations: [] }

  // Type lookup first (most common for "cannot find symbol" / class)
  const typeMatches = index.typesBySimpleName?.[name]
  if (typeMatches?.length) {
    return {
      found:     true,
      kind:      'type',
      locations: typeMatches.map(fqn => ({ fqn, ...index.typesByFqn?.[fqn] })),
    }
  }

  // Field lookup
  const fieldMatches = index.fieldsByName?.[name]
  if (fieldMatches?.length) {
    return { found: true, kind: 'field', locations: fieldMatches }
  }

  // Method lookup
  const methodMatches = index.methodsByName?.[name]
  if (methodMatches?.length) {
    return { found: true, kind: 'method', locations: methodMatches }
  }

  return { found: false, kind: null, locations: [] }
}

// ── Grouping ──────────────────────────────────────────────────────────────────

/**
 * Group issues by unique missing symbol.
 *
 * Only groups issues that have a non-null symbolName.
 * Group key = `${symbolCategory}::${symbolName}` (stable, human-readable).
 * Issues without symbolName are not grouped (caller treats them individually).
 *
 * @param {Array} issues — normalized Issue[] from gateIssueNormalizer
 * @returns {{ groups: Map<string, Issue[]>, uniqueSymbolCount: number }}
 */
export function groupIssuesBySymbol(issues) {
  const groups = new Map()
  let uniqueSymbolCount = 0

  for (const issue of issues) {
    const { symbolName, symbolCategory } = issue
    if (!symbolName) continue

    const key = `${symbolCategory || 'UNKNOWN'}::${symbolName}`
    if (!groups.has(key)) {
      groups.set(key, [])
      uniqueSymbolCount++
    }
    groups.get(key).push(issue)
  }

  return { groups, uniqueSymbolCount }
}

// ── Fix Panel hint ────────────────────────────────────────────────────────────

// Namespaces that suggest the symbol is an external / standard-library dependency.
const _EXTERNAL_PREFIXES = /^(java\.|javax\.|jakarta\.|org\.|com\.google\.|com\.fasterxml\.|io\.|net\.)/

/**
 * Derive a Fix Panel hint for a missing-symbol issue using the index.
 *
 * Returns one of:
 *   'internal'  — symbol found in the workspace (import/visibility issue, not a stub candidate)
 *   'external'  — symbol looks like an external library (suggest Mark as External)
 *   'stub'      — symbol not found anywhere (suggest Generate Stub)
 *   null        — no index / no symbolName (fall back to existing defaults)
 *
 * @param {object} issue  — normalized Issue
 * @param {object|null} index — schema v1 index (may be null)
 * @returns {'internal'|'external'|'stub'|null}
 */
export function getSymbolHint(issue, index) {
  const name = issue?.symbolName
  if (!name || !index) return null

  const resolved = resolveSymbol(name, index)
  if (resolved.found) return 'internal'

  // Check message for fully-qualified hints (e.g. "package java.math does not exist")
  const msg = (issue.message || '').toLowerCase()
  if (_EXTERNAL_PREFIXES.test(name) || msg.includes('does not exist')) {
    return 'external'
  }

  return 'stub'
}
